import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { IPaginationParameters, IPaginationResponse } from '../../../common';
import { ordersData } from '../data';
import {
  ICompleteOrderRequest,
  IOrderActionRequest,
  IOrderLookupsResponse,
  IOrderPaymentRequestResponse,
  IOrderResponse,
  IOrdersPaginationParameters,
  IOnStreetReservationResponse,
  IOnStreetReservationPaginationParameters,
  IOffStreetOrderResponse,
  ISettleOffStreetReservationOrderRequest,
  ISubscriptionOrderDetailsResponse,
  IOnStreetOrderDetailsResponse,
} from '../dtos';
import { IOrdersApiClient } from './i-orders-api.client';
import { ICreateSubscriptionOrderRequest } from '../dtos/requests/create-subscription-order.request';
import { ICreateOnStreetReservationOrderRequest } from '../dtos/requests/create-on-street-reservation-order.request';
import { IExtendOnStreetReservationOrderRequest } from '../dtos/requests/extend-on-street-reservation-order.request';
import { ICreatePublicOnStreetReservationOrderRequest } from '../dtos/requests/create-public-on-street-reservation-order.request';
import { IExtendPublicOnStreetReservationOrderRequest } from '../dtos/requests/extend-public-on-street-reservation-order.request';
import { IPublicOnStreetReservationResponse } from '../dtos/responses/public-on-street-reservation.response';
import { paymentsData } from '../../../payment-management/payments';
import { IPaymentResponse } from '../../../payment-management/payments/dtos/responses';
import { customerProfilesData, IPublicOffStreetReservationResponse, IReservationResponse } from '../../../customer-management';

@Injectable({
  providedIn: 'root'
})
export class OrdersInMemoryApiClient implements IOrdersApiClient {
  constructor(protected http: HttpClient) {}

  get(paginationParameters?: IOrdersPaginationParameters): Observable<IPaginationResponse<IOrderResponse>> {
    return of(ordersData.ordersResponse);
  }

  getById(id: string): Observable<IOrderResponse> {
    return of(ordersData.orderResponse);
  }

  getByIdPublic(id: string): Observable<IOrderResponse> {
    return of(ordersData.orderResponse);
  }

  lookups(): Observable<IOrderLookupsResponse> {
        return of(ordersData.lookups);
      }

  createSubscriptionOrder(request: ICreateSubscriptionOrderRequest): Observable<IOrderPaymentRequestResponse> {
    return of(ordersData.createOrderPaymentRequestResponse);
  }

  paymentSubscriptionOrder(id: string): Observable<IOrderPaymentRequestResponse> {
    return of(ordersData.createOrderPaymentRequestResponse);
  }

  getSubscriptionOrderDetails(orderId: string): Observable<ISubscriptionOrderDetailsResponse> {
    return of(ordersData.subscriptionOrderDetailsResponse);
  }

  createOnStreetReservationOrder(request: ICreateOnStreetReservationOrderRequest): Observable<IOrderPaymentRequestResponse> {
    return of(ordersData.createOrderPaymentRequestResponse);
  }

  extendOnStreetReservationOrder(id: string, request: IExtendOnStreetReservationOrderRequest): Observable<IOrderPaymentRequestResponse> {
    return of(ordersData.createOrderPaymentRequestResponse);
  }

  getOnStreetOrderDetails(orderId: string): Observable<IOnStreetOrderDetailsResponse> {
    return of(ordersData.onStreetOrderDetailsResponse);
  }

  getOnStreetReservations(paginationParameters?: IOnStreetReservationPaginationParameters): Observable<IPaginationResponse<IOnStreetReservationResponse>> {
    return of({
      items: [],
      paginationMetadata: {
        totalItemCount: 0,
        numberOfPages: 0,
        pageSize: paginationParameters?.pageSize || 10,
        order: paginationParameters?.sortOrder || 'Ascending',
        page: paginationParameters?.page || 1,
        hasNext: false,
        hasPrevious: false
      }
    });
  }

  getPayments(orderId: string): Observable<IPaginationResponse<IPaymentResponse>>{
    return of(paymentsData.paymentsResponse)
  }

  getLatestPayment(orderId: string): Observable<IPaymentResponse>{
    return of(paymentsData.paymentResponse)
  }

  getOffStreetOrders(paginationParameters?: IOrdersPaginationParameters): Observable<IPaginationResponse<IOffStreetOrderResponse>> {
    return of(ordersData.offStreetOrdersResponse);
  }

  getByIdOffStreetOrder(id: string): Observable<IOffStreetOrderResponse> {
    return of(ordersData.offStreetOrderResponse);
  }

  getOffStreetOrderByCode(code: string): Observable<IOffStreetOrderResponse> {
    return of(ordersData.offStreetOrderResponse);
  }

  settleOffStreetOrderForCustomer(code: string, request: ISettleOffStreetReservationOrderRequest): Observable<IOrderPaymentRequestResponse> {
    return of(ordersData.createOrderPaymentRequestResponse);
  }

  settleOffStreetOrderForAnonymous(code: string, request: ISettleOffStreetReservationOrderRequest): Observable<IOrderPaymentRequestResponse> {
    return of(ordersData.createOrderPaymentRequestResponse);
  }

  pickOffStreetOrder(code: string): Observable<IOrderPaymentRequestResponse> {
    return of(ordersData.createOrderPaymentRequestResponse);
  }

  getPublicOffStreetById(id: string): Observable<IPublicOffStreetReservationResponse> {
    return of(customerProfilesData.publicOffStreetReservationResponse);
  }

  getPublicOffStreetByCode(code: string): Observable<IPublicOffStreetReservationResponse> {
    return of(customerProfilesData.publicOffStreetReservationResponse);
  }

  createPublicOnStreetReservationOrder(request: ICreatePublicOnStreetReservationOrderRequest): Observable<IOrderPaymentRequestResponse> {
    return of(ordersData.createOrderPaymentRequestResponse);
  }

  extendPublicOnStreetReservationOrder(id: string, request: IExtendPublicOnStreetReservationOrderRequest): Observable<IOrderPaymentRequestResponse> {
    return of(ordersData.createOrderPaymentRequestResponse);
  }

  getPublicOnStreetReservation(id: string): Observable<IPublicOnStreetReservationResponse> {
    return of({
      autoIncrementalId: 1,
      status: 'Active',
      startTime: new Date(),
      endTime: new Date(Date.now() + 2 * 60 * 60 * 1000),
      totalHours: 2,
      remainingHours: 1,
      pricing: {
        subtotal: { amount: 100, currency: 'SAR' },
        discount: { discountValue: { amount: 0, currency: 'SAR' }, discountSource: 'None' },
        vat: { vatAmount: { amount: 15, currency: 'SAR' }, vatPercentage: 15 },
        total: { amount: 115, currency: 'SAR' }
      },
      vatPercentage: 15,
      supportsOnlinePayment: true,
      parkingArea: { id: 'test', englishName: 'Test Parking', arabicName: 'موقف اختبار' },
      servicePrice: {
        id: 'test-service-price',
        englishName: 'Standard Rate',
        arabicName: 'السعر القياسي',
        hourRate: { amount: 50, currency: 'SAR' }
      },
      details: [],
      vehicle: {
        licensePlateLetters: 'AB',
        licensePlateDigits: '123456',
        vehicleType: 'Sedan',
        country: {
          englishName: 'Saudi Arabia',
          arabicName: 'السعودية'
        },
        licensePlateType: {
          englishName: 'Personal',
          arabicName: 'شخصي'
        }
      }
    });
  }
}
